import React, { useState } from 'react';
import { Toggle, Shield } from 'lucide-react';
import { useCookieConsent } from '../../hooks/useCookieConsent';

interface CookieCategory {
  id: string;
  name: string;
  description: string;
  required: boolean;
  cookies: Array<{
    name: string;
    purpose: string;
    duration: string;
  }>;
}

const cookieCategories: CookieCategory[] = [
  {
    id: 'essential',
    name: 'Essential',
    description: 'Required for core site functionality',
    required: true,
    cookies: [
      {
        name: 'session_id',
        purpose: 'Session management and security',
        duration: 'Session'
      },
      {
        name: 'csrf_token',
        purpose: 'Security protection',
        duration: 'Session'
      }
    ]
  },
  {
    id: 'analytics',
    name: 'Analytics',
    description: 'Help us improve our BTM services',
    required: false,
    cookies: [
      {
        name: '_ga',
        purpose: 'Service usage tracking',
        duration: '2 years'
      }
    ]
  }
];

export default function CookiePreferenceCenter() {
  const { preferences, updatePreferences } = useCookieConsent();
  const [expanded, setExpanded] = useState<string | null>(null);

  const handleToggle = (categoryId: string) => {
    if (!cookieCategories.find(cat => cat.id === categoryId)?.required) {
      updatePreferences({ ...preferences, [categoryId]: !preferences[categoryId] });
    }
  };

  return (
    <div className="space-y-6">
      {cookieCategories.map((category) => (
        <div key={category.id} className="bg-gray-700/50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-lg font-semibold text-white">{category.name}</h3>
              <p className="text-sm text-gray-300">{category.description}</p>
            </div>
            <button
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                category.required || preferences[category.id] ? 'bg-purple-600' : 'bg-gray-500'
              }`}
              onClick={() => handleToggle(category.id)}
              disabled={category.required}
              aria-label={`Toggle ${category.name} cookies`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  category.required || preferences[category.id] ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          <button
            className="text-sm text-purple-400 hover:text-purple-300 mt-2"
            onClick={() => setExpanded(expanded === category.id ? null : category.id)}
          >
            {expanded === category.id ? 'Hide details' : 'Show details'}
          </button>
          {expanded === category.id && (
            <div className="mt-4 border-t border-gray-600 pt-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400">
                    <th className="pb-2">Cookie</th>
                    <th className="pb-2">Purpose</th>
                    <th className="pb-2">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {category.cookies.map((cookie) => (
                    <tr key={cookie.name} className="text-gray-300">
                      <td className="py-1">{cookie.name}</td>
                      <td className="py-1">{cookie.purpose}</td>
                      <td className="py-1">{cookie.duration}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}